import { GoogleOAuthProvider } from '@react-oauth/google';
import { useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import './app.css';
import ErrorBoundary from './components/ErrorBoundary';
import InstallPrompt from './components/InstallPrompt';
import ManagerProtectedRoutes from './components/ManagerProtectedRoutes';
import ProtectedRoutes from './components/ProtectedRoutes';
import RootRedirect from './components/RootRedirect';
import GroupPage from './page/group';
import CreateGroupPage from './page/group/create';
import { InventoryPage } from './page/inventory';
import InvitationPage from './page/invitation';
import { ManagerActivationsPage } from './page/manager/activations';
import ManagerApprovalsPage from './page/manager/approvals';
import CardsPage from './page/manager/cards';
import CreateCardPage from './page/manager/createCard';
import CreateGoalPage from './page/manager/createGoal';
import CreateShopItemPage from './page/manager/createShopItem';
import CreateTaskPage from './page/manager/createTask';
import ManagerGoalsPage from './page/manager/goals';
import ManagerHomePage from './page/manager/home';
import ManagerPurchasesPage from './page/manager/purchases';
import ManagerShopPage from './page/manager/shop';
import ManagerTasksPage from './page/manager/tasks';
import ManagerUsersPage from './page/manager/users';
import ProfilePage from './page/profile';
import ShopPage from './page/shop';
import TasksPage from './page/tasks';
import { pushNotificationService } from './service/push';
import useUserStore from './store/user';

const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

function App() {
  const user = useUserStore(state => state.user);

  useEffect(() => {
    if (user?.isManager) {
      document.body.className = 'manager-theme';
      import('./styles/manager.css');
    } else if (user) {
      document.body.className = 'user-theme';
      import('./styles/user.css');
    } else {
      document.body.className = 'user-theme';
      import('./styles/user.css');
    }
    return () => {
      document.body.className = '';
    };
  }, [user]);

  useEffect(() => {
    if (user?.isManager) {
      pushNotificationService.init().catch(error => {
        console.error('Failed to initialize push notifications:', error);
      });
    }
  }, [user]);

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <ErrorBoundary>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="/invitation/:code" element={<InvitationPage />} />

            {/* Protected routes */}
            <Route element={<ProtectedRoutes />}>
              {/* User routes */}
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/tasks" element={<TasksPage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/inventory" element={<InventoryPage />} />

              {/* Manager routes with group check */}
              <Route element={<ManagerProtectedRoutes />}>
                <Route path="/manager/group" element={<GroupPage />} />
                <Route
                  path="/manager/group/create"
                  element={<CreateGroupPage />}
                />
                <Route path="/manager/home" element={<ManagerHomePage />} />
                <Route path="/manager/tasks" element={<ManagerTasksPage />} />
                <Route path="/manager/goals" element={<ManagerGoalsPage />} />
                <Route path="/manager/shop" element={<ManagerShopPage />} />
                <Route
                  path="/manager/create-task"
                  element={<CreateTaskPage />}
                />
                <Route path="/manager/task/:id" element={<CreateTaskPage />} />
                <Route
                  path="/manager/create-shop-item"
                  element={<CreateShopItemPage />}
                />
                <Route
                  path="/manager/shop-item/:id"
                  element={<CreateShopItemPage />}
                />
                <Route
                  path="/manager/create-goal"
                  element={<CreateGoalPage />}
                />
                <Route path="/manager/goal/:id" element={<CreateGoalPage />} />
                <Route path="/manager/cards" element={<CardsPage />} />
                <Route
                  path="/manager/create-card"
                  element={<CreateCardPage />}
                />
                <Route path="/manager/card/:id" element={<CreateCardPage />} />
                <Route
                  path="/manager/purchases"
                  element={<ManagerPurchasesPage />}
                />
                <Route
                  path="/manager/approvals"
                  element={<ManagerApprovalsPage />}
                />
                <Route
                  path="/manager/activations"
                  element={<ManagerActivationsPage />}
                />
                <Route path="/manager/users" element={<ManagerUsersPage />} />
              </Route>
            </Route>
          </Routes>
          <InstallPrompt />
        </BrowserRouter>
      </ErrorBoundary>
    </GoogleOAuthProvider>
  );
}

export default App;
